import { useMemo, useState } from "react";
import { Container, Col, Row } from "reactstrap";
import { dayLabel } from "components/day/dates";
import { BreadcrumbItem } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";
import * as Notify from "@repoflow/form-schema/notify";
import * as EntryQL from "queries/entry";
import { Component as Form } from "@repoflow/form-schema";
import { MEALS } from "@common/schema/entry/fields";
import { fitMeal } from "@common/schema/settings/fields";
import { mealOptions } from "components/day/summary";
import { useMeals } from "components/settings/meals";
import { FoodPicker } from "components/foods/picker";
import { Mutation, Button } from "@repoflow/components/section";
import * as appWidgets from "@shared/widgets/register";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>Add food</BreadcrumbItem>;

// The field list, hoisted out of the component: a `config` literal in JSX is a
// new object every render, and the form rebuilds its widget tree under a new
// one — see wfws' workflow editor for what that costs a field being typed in.
//
// `date` is not asked for. The day screen this was opened from IS the date, and
// a second place to choose it would be a second place to get it wrong.
//
// `meal` offers only the meals the settings show, so the list is built per set
// of meals (fieldsFor) and memoised on it rather than hoisted whole.
export const fieldsFor = (options) => ({
  "/Public/Entry/Create": {
    fields: [
      { property: "food" },
      { property: "serving", description: "Free text — \"1 cup\", \"200 g\", \"2 slices\"." },
      { property: "meal", options },
      { property: "calories" },
      { property: "protein" },
      { property: "carbs" },
      { property: "fat" },
      { property: "fiber" },
    ],
  },
});

// The meal a person most likely means, when the link did not say: the one the
// clock is nearest to, or the nearest one shown to it. Only a default — the
// field is right there.
export const mealForHour = (hour, shown = MEALS) => {
  if (hour < 11) return fitMeal("breakfast", shown);
  if (hour < 16) return fitMeal("lunch", shown);
  if (hour < 21) return fitMeal("dinner", shown);
  return fitMeal("snack", shown);
};

// The settings are read before the form is built, so its starting meal is one
// it offers: a Mutation takes `initial` once.
export const Content = (props) => {
  const shown = useMeals();
  return shown ? <NewEntry {...props} shown={shown} /> : null;
};

const NewEntry = ({ match: { params: { date } }, shown }) => {
  const history = useHistory();
  const { search } = useLocation();
  const asked = new URLSearchParams(search).get("meal");

  const initial = {
    date,
    meal: shown.includes(asked) ? asked : mealForHour(new Date().getHours(), shown),
    food: "",
    serving: "",
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
  };

  return (
    <Mutation
      initial={initial}
      target={{ $ref: "/Public/Entry/Create" }}
      mutation={EntryQL.create}
      update={EntryQL.update()}
      onMutate={({ state: { values }, handler }) => {
        handler({ variables: { input: { ...values, date } } });
      }}
      onCompleted={() => {
        // replace(), not push(): Back from the day should not reopen a form for
        // something already logged.
        history.replace(`/day/${date}`);
      }}
      onError={(error) => {
        Notify.error(String(error));
      }}
    >
      {MutationHandler}
    </Mutation>
  );
};

// The Mutation hands its children the section context — `schema`, `state`,
// `mutate`, `target` — and NOT a Fields component. The form is built here from
// those, which is the shape every other screen in this app uses.
//
// Rendered by the Mutation as an element of THIS type, so the meals are read
// here (from the cache Content already filled) rather than passed through an
// inline arrow, which would be a new type and a remounted form every render.
const MutationHandler = ({ schema, state, mutate, target }) => {
  const { values, setValues } = state;
  // The form keeps what it shows in its own state once mounted, so values set
  // from outside it — a remembered food picked — reach it by remounting it.
  const [picked, setPicked] = useState(0);
  const shown = useMeals() || MEALS;
  const fields = useMemo(() => fieldsFor(mealOptions(shown)), [shown]);

  return (
    <Container className="mt-4" style={{ maxWidth: 760 }}>
      {/* Which day this lands on, said once, since the form has no date field. */}
      <h4 className="mb-4">Add food to {dayLabel(values.date).replace(/^(Today|Yesterday|Tomorrow)$/, (d) => d.toLowerCase())}</h4>
      {/* A food logged before fills the form in one tap (components/foods). */}
      <FoodPicker
        typed={values.food}
        onPick={(food) => {
          setValues({
            ...values,
            food: food.name,
            serving: food.serving,
            calories: food.calories,
            protein: food.protein,
            carbs: food.carbs,
            fat: food.fat,
            fiber: food.fiber,
          });
          setPicked((n) => n + 1);
        }}
      />
      <Row>
        <Col>
          <Form
            key={picked}
            schema={schema}
            target={target}
            values={values}
            widgets={appWidgets}
            config={fields}
            onSubmit={() => mutate()}
            onChange={(vals) => setValues(vals)}
          >
            <Button type="submit">Add</Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};
